import { AIPhoneCallButton } from './AIPhoneCallButton'
import { formatDateInTimeZone } from '../../../lib/formatDateInTimeZone'
import { ProviderModel } from '../../reduxToolkit/providersSlice'
import type { ConversationWithMessagesAndPhoneCalls } from '../../reduxToolkit/conversationsSelectors'
import { maxBy, minBy } from 'lodash'

interface AIPhoneCallWidgetProps {
  expenseId: string
  provider: ProviderModel,
  isWhitelisted: boolean,
  existingConversation?: ConversationWithMessagesAndPhoneCalls
}

export function AIPhoneCallWidget({
  expenseId,
  provider,
  isWhitelisted,
  existingConversation,
}: AIPhoneCallWidgetProps) {
  // Get most recent phone call
  const mostRecentPhoneCall = maxBy(existingConversation?.phone_calls || [], 'created_at') || null
  // Determine if button should be shown based on phone call status
  const shouldShowButton = (() => {
    if (!mostRecentPhoneCall) {
      // a. No AI phone call for this expense yet
      return true
    }

    const status = mostRecentPhoneCall.status
    const analysisOutcome = mostRecentPhoneCall.analysis_outcome

    // b. Phone call that errored
    const errorStatuses = ['FAILED', 'NO_ANSWER', 'BUSY', 'CANCELED']
    if (errorStatuses.includes(status)) {
      return true
    }

    // c. Phone call that succeeded but analysis_outcome was failure
    if (status === 'COMPLETED' && analysisOutcome === 'failure') {
      return true
    }

    // Hide button for active calls or successful calls
    return false
  })()

  // Get short header text for the status
  const getStatusHeader = () => {
    if (!mostRecentPhoneCall) {
      return 'AI Phone Call'
    }

    const status = mostRecentPhoneCall.status
    const analysisOutcome = mostRecentPhoneCall.analysis_outcome

    // Active call states
    if (['REQUESTED', 'QUEUED'].includes(status)) {
      return 'Call Requested'
    }

    if (['RINGING', 'IN_PROGRESS'].includes(status)) {
      return 'Call In Progress'
    }

    // Terminal states
    if (status === 'COMPLETED') {
      if (analysisOutcome === 'success') {
        return 'Call Successful'
      } else if (analysisOutcome === 'failure') {
        return 'Call Completed'
      } else {
        return 'Call Completed'
      }
    }

    if (status === 'FAILED') {
      return 'Call Failed'
    }

    if (status === 'NO_ANSWER') {
      return 'No Answer'
    }

    if (status === 'BUSY') {
      return 'Line Busy'
    }

    if (status === 'CANCELED') {
      return 'Call Canceled'
    }

    return 'Unknown Status'
  }

  // Get detailed explanation text
  const getDetailedText = () => {
    if (!mostRecentPhoneCall) {
      return 'Request an AI-powered phone call to contact the provider and request an itemized bill for this expense.'
    }

    const status = mostRecentPhoneCall.status
    const analysisOutcome = mostRecentPhoneCall.analysis_outcome
    const analysisSummary = mostRecentPhoneCall.analysis_summary
    const createdAt = mostRecentPhoneCall.created_at
    const formattedDate = createdAt ? formatDateInTimeZone(createdAt, { format: 'MM/dd/yyyy h:mm a' }) : ''

    // Active call states
    if (['REQUESTED', 'QUEUED'].includes(status)) {
      return `AI call requested ${formattedDate}. Waiting to be initiated...`
    }

    if (['RINGING', 'IN_PROGRESS'].includes(status)) {
      return `AI call in progress ${formattedDate}. Please wait while we contact the provider...`
    }

    // Terminal states
    if (status === 'COMPLETED') {
      if (analysisOutcome === 'success') {
        return analysisSummary || 'AI call completed successfully. The itemized bill has been requested from the provider.'
      } else if (analysisOutcome === 'failure') {
        return analysisSummary || 'AI call completed but was unable to successfully request the itemized bill. You may try calling again.'
      } else {
        // Completed but not analyzed yet
        return 'AI call completed. Analyzing the transcript to determine if the itemized bill was successfully requested...'
      }
    }

    if (status === 'FAILED') {
      const errorMsg = mostRecentPhoneCall.error ? `: ${mostRecentPhoneCall.error}` : ''
      return `AI call failed ${formattedDate}${errorMsg}. You may try calling again.`
    }

    if (status === 'NO_ANSWER') {
      return `AI call attempted ${formattedDate} but there was no answer. You may try calling again later.`
    }

    if (status === 'BUSY') {
      return `AI call attempted ${formattedDate} but the line was busy. You may try calling again later.`
    }

    if (status === 'CANCELED') {
      return `AI call was canceled ${formattedDate}.`
    }

    return 'Unknown call status'
  }

  return (
    <div
      className="p-3"
      style={{
        backgroundColor: '#f8f9fa',
        borderBottomLeftRadius: '0.375rem',
        borderBottomRightRadius: '0.375rem',
      }}
    >
      {/* Header row with status and button */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>{getStatusHeader()}</strong>
        {shouldShowButton && (
          <AIPhoneCallButton
            expenseId={expenseId}
            provider={provider}
            isWhitelisted={isWhitelisted}
            existingConversation={existingConversation}
          />
        )}
      </div>

      {/* Detailed explanation text */}
      <div className="small text-muted">
        {getDetailedText()}
      </div>
    </div>
  )
}
